import { Link } from 'react-router-dom';
import { MetricCard } from '../components/MetricCard';
import { ModuleCard } from '../components/ModuleCard';
import { Notice } from '../components/Notice';
import type { ModuleId } from '../core/types';
import { useApp } from '../app/AppContext';
import { buildModuleScorePoints, buildOverallScorePoints } from '../training';

const moduleIds: ModuleId[] = ['read-switch', 'central-peripheral', 'player-track', 'moving-target'];
const scoreLabels: Record<ModuleId, string> = {
  'read-switch': '動きの識別',
  'central-peripheral': '中央・周辺',
  'player-track': '複数対象の追跡',
  'moving-target': '動く記号',
};

export function DashboardPage() {
  const app = useApp();
  const calibrated = Boolean(app.calibration && app.environment);
  const completed = app.sessions.filter((session) => session.status === 'completed' && (app.summariesBySession[session.id]?.length ?? 0) > 0);
  const overall = buildOverallScorePoints(app.sessions, app.summariesBySession).at(-1);
  const latestByModule = new Map(moduleIds.map((moduleId) => [
    moduleId,
    buildModuleScorePoints(moduleId, app.sessions, app.summariesBySession).at(-1),
  ]));

  return (
    <div className="page">
      <section className="hero-panel" aria-labelledby="hero-title">
        <div className="hero-copy">
          <div className="eyebrow">自由練習</div>
          <h1 id="hero-title">今日練習する課題を選びます</h1>
          <p>4課題をまとめて行うか、1課題だけに集中できます。実施した結果はすぐに履歴とスコアへ反映されます。</p>
          <div className="hero-actions">
            {calibrated
              ? <Link className="button" to="/session">4課題をまとめて練習</Link>
              : <Link className="button" to="/calibration">準備を始める</Link>}
            <Link className="text-link" to="/history">スコアの推移を見る</Link>
          </div>
        </div>
        <div className="hero-summary" aria-label="現在の記録">
          <span>保存済みの練習</span>
          <strong>{completed.length}回</strong>
          <small>{overall ? `最新の総合点 ${Math.round(overall.value)} / 100` : calibrated ? '最初の練習から記録します' : '先に画面と視距離を設定します'}</small>
        </div>
      </section>

      <div className="section-row"><div><span className="section-kicker">単体練習</span><h2>1つの課題を選ぶ</h2></div></div>
      <section className="module-grid" aria-label="トレーニング課題一覧">
        {moduleIds.map((id) => <ModuleCard key={id} id={id} href={calibrated ? `/session?module=${id}` : '/calibration'} />)}
      </section>

      <div className="section-row"><div><span className="section-kicker">最新記録</span><h2>現在のスコア</h2></div><Link className="text-link" to="/history">表示期間を選んで見る</Link></div>
      <section className="grid-4 dashboard-scores" aria-label="最新スコア">
        <MetricCard label="総合点" badge="TOTAL" value={overall ? Math.round(overall.value) : '—'} unit={overall ? '/ 100' : ''} detail="同じ環境での各課題の最新点を平均" />
        {moduleIds.map((moduleId) => {
          const point = latestByModule.get(moduleId);
          return <MetricCard key={moduleId} label={scoreLabels[moduleId]} value={point ? Math.round(point.value) : '—'} unit={point ? '/ 100' : ''} detail={point ? new Intl.DateTimeFormat('ja-JP', { dateStyle: 'medium' }).format(new Date(point.date)) : 'まだ記録がありません'} />;
        })}
      </section>

      <Notice title="スコアはこのアプリ内の練習度です。" tone="warning">
        正答率と到達した難易度を0〜100にまとめた記録用の値で、医学的な正常値やサッカー能力の評価ではありません。
      </Notice>
    </div>
  );
}
